import React, {useState, useCallback, useEffect, useMemo} from 'react';

import {observer} from 'mobx-react';
import {ObservableMap} from 'mobx';

import {useStores} from '../../../Hooks';
import {routes} from '../../../App';
import {history} from '../../../index';
import {procedureTypeEnum} from '../../../serverClientShared/sharedEnums';
import SettingsModalShell from '../SettingsShared/SettingsModalShell';
import useProjectSettings from './useProjectSettings';
import useRoleAccessRules from './useRoleAccessRules';
import useConnectedFlowsState from './useConnectedFlowsState';
import ProjectInfoTab from './ProjectInfoTab';
import ProjectAdvancedTab from './ProjectAdvancedTab';

const PROJECT_INFO_TAB = {key: 'projectInfo', label: 'Project Info', subtitle: 'A project has its own digital twins, procedures and media.'};
const ADVANCED_TAB = {key: 'advanced', label: 'Advanced Settings', subtitle: 'Connections, contact info and project defaults.'};
const TABS = [PROJECT_INFO_TAB, ADVANCED_TAB];

const ProjectSettingsModal = observer(() => {
    const {modalStore, projectStore, dataStore, generalStore} = useStores();
    const [activeTab, setActiveTab] = useState(0);
    const [threeDModels, setThreeDModels] = useState([]);
    const settings = useProjectSettings();
    const projectId = settings.project?.ProjectIdentifier;
    const projectName = settings.project?.ProjectName;

    const proceduresExpiration = projectId ? dataStore.projectsProceduresExpirationMap.get(projectId) : 0;
    const roleAccess = useRoleAccessRules(projectId, projectName, generalStore.workspace, proceduresExpiration);
    const connectedFlows = useConnectedFlowsState(projectId, settings.isMirrored);

    useEffect(() => {
        if (!projectId) return;
        const map = dataStore.projectsProceduresMap.get(projectId) || new ObservableMap();
        const fusion = Array.from(map.values()).filter((p) => p.IsPublished && p.ProcedureType === procedureTypeEnum.FUSION);
        setThreeDModels(fusion);
    }, [projectId, dataStore.projectsProceduresMap]);

    const handleEnter = useCallback(() => {
        const p = modalStore.modalData?.project || projectStore.currentProject;
        if (!p) return;
        settings.initFromProject(p);
        setActiveTab(0);
    }, [modalStore.modalData?.project, projectStore.currentProject]);

    const closeModal = useCallback(() => {
        modalStore.isProjectSettingsModalOpen = false;
        modalStore.resetModalData();
    }, [modalStore]);

    const handleDelete = () => {
        const p = settings.project;
        if (!p) return;
        const t = generalStore.translate.bind(generalStore);
        modalStore.modalData = {
            title: t('deleteProject') || 'Delete Project',
            bodyText: `${t('deleteProjectConfirmSentence1_beforeName') || 'Are you sure you want to delete'} "${p.ProjectName}"? ${t('thisActionCannotBeUndone') || 'This action cannot be undone.'}`,
            confirmText: t('delete') || 'Delete',
            isDelete: true,
            confirmActions: async () => {
                generalStore.isLoaderOpen = true;
                try {
                    const success = await projectStore.deleteProject(p.ProjectIdentifier);
                    if (success) {
                        closeModal();
                        history.push(routes.MAIN_DASHBOARD);
                    } else {
                        modalStore.showErrorMessage(t('problemDeletingProject') || 'Problem deleting project');
                    }
                } catch (error) {
                    console.error('Failed to delete project:', error);
                    modalStore.showErrorMessage(t('problemDeletingProject') || 'Problem deleting project');
                }
                generalStore.isLoaderOpen = false;
            },
        };
        modalStore.isGeneralConfirmActionDialogOpen = true;
    };

    const isDirty = settings.isDirty || roleAccess.isDirty || connectedFlows.isDirty;

    const handleSave = useCallback(async () => {
        if (connectedFlows.isDirty) await connectedFlows.save();
        await settings.save();
        if (roleAccess.isDirty) await roleAccess.save();
    }, [settings, roleAccess, connectedFlows]);

    const handleDiscard = useCallback(() => {
        settings.discard();
        roleAccess.discard();
        connectedFlows.discard();
    }, [settings, roleAccess, connectedFlows]);

    return (
        <SettingsModalShell
            openControl="isProjectSettingsModalOpen"
            title="Edit Project Settings"
            subtitle={TABS[activeTab].subtitle}
            tabs={TABS}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onEnter={handleEnter}
            isDirty={isDirty}
            onSave={handleSave}
            onDiscard={handleDiscard}
            onDelete={settings.isMirrored ? undefined : handleDelete}
            closeModal={closeModal}
            isContentReady={settings.isAccessLoaded}
        >
            {activeTab === 0
                ? <ProjectInfoTab settings={settings} threeDModels={threeDModels} />
                : <ProjectAdvancedTab settings={settings} threeDModels={threeDModels} roleAccess={roleAccess} connectedFlows={connectedFlows} />
            }
        </SettingsModalShell>
    );
});

export default ProjectSettingsModal;
